import { useState } from 'react';

import { Box, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { sessionStore } from '~atoms/sessionAtom';
import { LanguageSelector } from '~components';
import { UsersService } from '~features/users';
import ApiError from '~services/apiError';
import { getService } from '~services/serviceFactory';
import { Dimens } from '~themes';

import LoginForm from '../components/loginForm';
import LoginSplash from '../components/loginSplash';
import OtpInputOverlay from '../components/otpInputOverlay';
import {
	parseTokenResponseTypeToOidcTokens,
	TokenResponseType,
} from '../interfaces/tokenResponseType';
import SignInService from '../signInService';

const signInService = getService(SignInService);
const usersService = getService(UsersService);

function LoginPageContainer() {
	const { t } = useTranslation();

	const [showOtpInput, setShowOtpInput] = useState(false);
	const [otpLength, setOtpLength] = useState(8);
	const [otpError, setOtpError] = useState('');
	const [loginError, setLoginError] = useState('');
	const [email, setEmail] = useState('');
	const [isVerifying, setIsVerifying] = useState(false);
	const [isCheckingEmail, setIsCheckingEmail] = useState(false);
	const [continuationToken, setContinuationToken] = useState('');

	const handleSignInWithEmail = async (email: string) => {
		setEmail(email);
		setIsCheckingEmail(true);
		signInService
			.signInStart({ email })
			.then((startResult) => {
				if (startResult.challenge_type == 'redirect') {
					setLoginError(t('pleaseSignInWithMicrosoft', { ns: 'auth' }));
					setIsCheckingEmail(false);
				} else {
					setContinuationToken(startResult.continuation_token);
					triggerOtpEmail(startResult.continuation_token);
				}
			})
			.catch((error: ApiError) => {
				const errorCode = (error.axiosError.response?.data as { error?: string } | undefined)
					?.error;
				if (errorCode === 'user_not_found') {
					setLoginError(t('loginFailed', { ns: 'errors' }));
				} else {
					setLoginError(t('somethingWentWrong', { ns: 'errors' }));
				}
				setIsCheckingEmail(false);
			});
	};

	const triggerOtpEmail = async (continuationTokenValue: string) => {
		const challengeResult = await signInService.signInChallenge({
			continuationToken: continuationTokenValue,
		});
		setOtpLength(Number(challengeResult.code_length));
		setShowOtpInput(true);
		setContinuationToken(challengeResult.continuation_token);
		setIsCheckingEmail(false);
	};

	const handleVerify = async (code: string) => {
		setIsVerifying(true);

		await signInService
			.signInTokenRequest({
				oob: code,
				continuationToken: continuationToken,
			})
			.then(async (tokens: TokenResponseType) => {
				// After the token are set and the user is loaded, the [PublicRoute] will notice
				// we now have a session, and redirect to the dashboard.
				sessionStore.setLoadingUser(true, true);
				sessionStore.setOidcTokens(parseTokenResponseTypeToOidcTokens(tokens));
				const connectUser = await usersService.getCurrentUser();
				sessionStore.setConnectUser(connectUser);
				sessionStore.setLoadingUser(false, false);
				setContinuationToken('');
			})
			.catch((_) => {
				setOtpError(t('invalidOtpError', { ns: 'auth' }));
			})
			.finally(() => {
				setIsVerifying(false);
			});
	};

	const handleGoBack = () => {
		setShowOtpInput(false);
		setContinuationToken('');
	};

	const handleOtpOverlayFaded = () => {
		setOtpError('');
	};

	return (
		<>
			<meta name='viewport' content='width=device-width, initial-scale=1, maximum-scale=1'></meta>
			<Stack direction='row' sx={{ height: '100vh', width: '100vw' }}>
				<Stack
					direction='row'
					justifyContent='center'
					sx={{
						flex: '0 1 1000px',
						minWidth: {
							xs: '100%',
							sm: '400px',
						},
					}}
				>
					<LoginForm
						isLoading={isCheckingEmail}
						onSignInWithEmail={handleSignInWithEmail}
						logInError={loginError}
					/>
				</Stack>
				<Box
					sx={{
						height: '100%',
						width: '100%',
						background: 'black',
						transition: 'opacity 400ms ease',
					}}
				>
					<LoginSplash />
				</Box>
			</Stack>
			<OtpInputOverlay
				show={showOtpInput}
				email={email}
				otpLength={otpLength}
				error={otpError}
				isLoading={isVerifying}
				onVerify={async (otpCode) => await handleVerify(otpCode)}
				onGoBack={handleGoBack}
				onExited={handleOtpOverlayFaded}
				onResend={() => triggerOtpEmail(continuationToken)}
			/>
			<Box sx={{ position: 'absolute', top: Dimens.spacing5xl, right: Dimens.spacing5xl }}>
				<LanguageSelector iconColor={showOtpInput ? 'black' : 'white'} />
			</Box>
		</>
	);
}

export default LoginPageContainer;
